/**
 * Singleton WebSocket Service for Realtime Notifications
 * Keeps a single WebSocket connection across the application tab
 * and distributes NOTIFY / UN_NOTIFY / WS_CONNECTED events to all active listeners.
 */

const WS_RECONNECT_BASE_MS = 3000;
const listeners = new Set();
let ws = null;
let currentAccountId = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let isIntentionallyClosed = false;

function getWsHost() {
  const apiBase = process.env.VUE_APP_API_SERVICE_URL || '/api/';
  const url = new URL(apiBase, window.location.origin);
  return (url.protocol === 'https:' ? 'wss' : 'ws') + '://' + url.host;
}

function getAccountId() {
  try {
    const raw = localStorage.getItem('current-account');
    if (!raw) return null;
    const acc = JSON.parse(raw);
    return acc && acc.id ? String(acc.id) : null;
  } catch (e) {
    return null;
  }
}

function scheduleReconnect() {
  if (reconnectTimer || isIntentionallyClosed) return;
  const delay = Math.min(30000, WS_RECONNECT_BASE_MS * Math.pow(1.5, reconnectAttempts));
  reconnectAttempts++;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

function connect() {
  const token = localStorage.getItem('token');
  const accountId = getAccountId();
  if (!token || !accountId) {
    disconnect();
    return;
  }

  // Already connected or connecting for this account
  if (ws && currentAccountId === accountId && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  if (ws) {
    ws.onclose = null;
    ws.close();
    ws = null;
  }

  currentAccountId = accountId;
  isIntentionallyClosed = false;

  try {
    const wsUrl = `${getWsHost()}/ws/notify/${accountId}`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      reconnectAttempts = 0;
      notifyListeners({ type: 'WS_CONNECTED' });
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        notifyListeners(payload);
      } catch (e) {
        console.error('Invalid notification websocket message', e);
      }
    };

    socket.onclose = () => {
      ws = null;
      if (!isIntentionallyClosed) {
        scheduleReconnect();
      }
    };

    socket.onerror = () => {
      if (socket) {
        socket.close();
      }
    };

    ws = socket;
  } catch (e) {
    console.error('WebSocket connect error', e);
    scheduleReconnect();
  }
}

function disconnect() {
  isIntentionallyClosed = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    ws.onclose = null;
    ws.close();
    ws = null;
  }
  currentAccountId = null;
  reconnectAttempts = 0;
}

function notifyListeners(payload) {
  for (const listener of listeners) {
    try {
      listener(payload);
    } catch (err) {
      console.error('Error in notification listener', err);
    }
  }
}

// Global listener for logout event
if (typeof window !== 'undefined') {
  window.addEventListener('user-logged-out', () => {
    disconnect();
  });
}

export default {
  subscribe(callback) {
    listeners.add(callback);
    connect();
    return () => {
      listeners.delete(callback);
    };
  },
  connect,
  disconnect,
};
